import {
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Branch, branchesApi } from '@texas/api/endpoints/branchesApi';
import { Role } from '@texas/api/endpoints/userApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { useActiveSession } from '@texas/hooks/useSession';
import { hasRole } from '@texas/utils/helpers/roleHelpers';
import { AvailableBranches } from './AvailableBranches';
import { BranchModal } from './BranchModal';
import { branchEvents } from './events';

export function Branches() {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const canEdit = hasRole(currentUser.role, [Role.SystemAdministrator]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Branch | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch, set, loading, error } = useApiResource(
    branchesApi.getBranchesForOrganization,
  );

  useEffect(() => {
    refetch(currentUser.organizationId);
  }, [currentUser.organizationId, refetch]);

  const branches = useMemo(() => {
    const normalized = filter.trim().toLowerCase();
    return (data ?? [])
      .filter((branch) => branch.name.toLowerCase().includes(normalized))
      .sort((left, right) => left.displayOrder - right.displayOrder);
  }, [data, filter]);

  return (
    <ConfigurationPage
      title={t('configuration.navigation.branches')}
      toolbar={
        <SearchFilterInput
          value={filter}
          style="entityDirectory"
          onChange={setFilter}
        />
      }
    >
      {selected && (
        <BranchModal
          branch={selected}
          canEdit={canEdit}
          isOpen={isOpen}
          onClose={onClose}
          onSaved={() => {
            refetch(currentUser.organizationId);
            branchEvents.changed.dispatch();
          }}
        />
      )}
      <Flex direction="column" gap="4">
        {canEdit && (
          <AvailableBranches
            onBranchCreated={(branch) => {
              set((current) => [...(current ?? []), branch]);
              branchEvents.changed.dispatch();
            }}
          />
        )}
        <ErrorDetails error={error} />
        <Table variant="entity-directory">
          <Thead>
            <Tr>
              <Th>{t('general.name')}</Th>
              <Th>{t('configuration.garpBatching')}</Th>
              <Th>{t('branch.displayOrder')}</Th>
              <Th>{t('branch.defaultInternalSupplier')}</Th>
              <Th>{t('configuration.defaultDecimalsInPrice')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {!loading && branches.length === 0 && (
              <Tr>
                <Td colSpan={5}>{t('general.noData')}</Td>
              </Tr>
            )}
            {branches.map((branch) => (
              <Tr
                key={branch.id}
                cursor="pointer"
                role="button"
                tabIndex={0}
                onClick={() => {
                  setSelected(branch);
                  onOpen();
                }}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  setSelected(branch);
                  onOpen();
                }}
              >
                <Td>{branch.name}</Td>
                <Td>{branch.enableErpBatching ? t('general.enabled') : ''}</Td>
                <Td>{branch.displayOrder}</Td>
                <Td>{branch.mainInternalSupplierName}</Td>
                <Td>{branch.defaultDecimalsInPrice}</Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </ConfigurationPage>
  );
}
